import React, {FC, useEffect, useState} from 'react';

import {sendDesktopAnalyticsEvent} from '@optimaxdev/analytics/desktop/lib/sendAnalyticsEvent/index';
import {CtaButton} from '@optimaxdev/design-system';
import {useRecurly} from '@recurly/react-recurly';
import classNames from 'classnames';

import {connect} from 'react-redux';

import {CloudinaryImage} from 'components/cloudinaryImage/cloudinaryImage';
import {CHECKOUT_PAYMENT_STEP} from 'constants/analytics/events/checkout';
import {PlacingOrderScreen} from 'desktop/components/placingOrderScreen/placingOrderScreen';
import {ApplicationStoreType} from 'desktop/store';
import {RecurlyErrorType} from 'desktop/types/recurlyTypes';
import {OrderErrorType} from 'types/completeOrderType';

import {PAYMENTS} from '../../constants/paymentStep';
import {setOptionPaymentMethod} from '../../store/reducers/analytics/analytics';
import {sendPayerInfoToSaga} from '../../store/reducers/paymentStep/payerInfo/payerInfo';
import {
    getCompleteOrderErrors,
    getIsPlacingOrder,
} from '../../store/selectors/completeOrder/completeOrder';
import {getUserFrendlyRecurlyError} from '../../utils/paymentRecurlyUtils/paymentRecurlyUtils';
import {PaymentFooterSubscriptionDisclaimerConnected} from '../paymentFooterSubscriptionDisclaimer/paymentFooterSubscriptionDisclaimer';

import PayPalIcon from './img/paypal.svg?url';

import s from './paymentRecurlyPayPalForm.scss';

export type PropsType = {
    isLoading: boolean;
    title: string;
    runSavePayment: (data: {title: string; token: string; method: string}) => void;
    sendAnalytics: () => void;
    completeOrderErrors?: Array<OrderErrorType>;
};

/**
 * Provides handler and set of inputs from recurly to get a token
 * @param {PropsType} props - current props
 * @return {Node} component
 */
export const PaymentRecurlyPayPalForm: FC<PropsType> = ({
    completeOrderErrors,
    isLoading,
    runSavePayment,
    sendAnalytics,
    title,
}) => {
    const [isActive, setIsActive] = useState(false);
    const [error, setError] = useState<Maybe<RecurlyErrorType>>(null);
    const recurly = useRecurly();
    const payPal = recurly.PayPal({
        display: {displayName: title},
    });

    useEffect(() => {
        payPal.on('token', (token: {id: string; type: string}) => {
            setError(null);

            runSavePayment({
                method: PAYMENTS.METHOD_RECURLY_PAYPAL,
                title,
                token: token.id,
            });

            setIsActive(false);
        });

        payPal.on('error', (e: RecurlyErrorType) => {
            setIsActive(false);
            sendDesktopAnalyticsEvent(
                CHECKOUT_PAYMENT_STEP,
                {
                    // @ts-expect-error type from recurly library is incorrect(
                    message: `Error – ${getUserFrendlyRecurlyError(err)}`,
                    method: 'Recurly Paypal',
                },
                false,
            );
            setError(e);
        });

        payPal.on('cancel', () => {
            setIsActive(false);
        });
    }, [payPal]);

    useEffect(() => {
        if (completeOrderErrors && completeOrderErrors.length) {
            const errorMessage = completeOrderErrors[0].title;
            setError({
                code: errorMessage,
                details: [],
                fields: [],
                message: errorMessage,
                name: errorMessage,
            });
        }
    }, [completeOrderErrors]);

    const onClick = () => {
        payPal.start();
        setIsActive(true);
        sendDesktopAnalyticsEvent(
            CHECKOUT_PAYMENT_STEP,
            {
                message: `CTA - Recurly (PayPal)`,
                method: 'Payment',
            },
            true,
        );
        sendAnalytics();
    };

    return (
        <div className={s.wrapper}>
            <CtaButton
                ariaLabel="Place Order"
                className={s.button}
                isDisabled={isActive || isLoading}
                isLoading={isActive || isLoading}
                onClick={onClick}
                size="medium"
                type="button"
            >
                <CloudinaryImage
                    alt="PayPal"
                    className={s.paypalIcon}
                    height={22}
                    src={PayPalIcon}
                    width={69}
                />
                <span className={s.buttonText}>Checkout</span>
            </CtaButton>

            {(isActive || isLoading) && <PlacingOrderScreen />}

            <p className={classNames(s.text, s.noticeText)}>
                Click the button to be redirected to PayPal.com & complete your purchase.
            </p>

            {error && <p className={s.errorMsg}>{getUserFrendlyRecurlyError(error)}</p>}

            <PaymentFooterSubscriptionDisclaimerConnected />
        </div>
    );
};

/**
 * Map state to props
 * @param {ApplicationStoreType} store - redux store
 * @returns {PropsType} props
 */
const mapStateToProps = (store: ApplicationStoreType) => ({
    completeOrderErrors: getCompleteOrderErrors(store),
    isLoading: getIsPlacingOrder(store),
});

export const PaymentRecurlyPayPalFormConnected = connect(mapStateToProps, {
    runSavePayment: sendPayerInfoToSaga,
    sendAnalytics: setOptionPaymentMethod,
})(PaymentRecurlyPayPalForm);
